/* Brand → generic confirmations learned from Bulk Data: list (last 30 days) + one-click undo. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { History, Undo2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API, getAuthHeader } from "@/App";

const LEARN_LABEL = { alias: "Added as alias", created: "New row", updated: "Generic changed" };

export const undoLearn = async ({ logId, batchId }) => {
  const url = batchId ? `${API}/bulk-data/learn-log/batch/${batchId}/undo` : `${API}/bulk-data/learn-log/${logId}/undo`;
  try {
    const r = await axios.post(url, {}, { headers: getAuthHeader() });
    toast.success(`Undone ${r.data.undone} · ${r.data.file_rows_reverted} file row(s) back to MEDICINE`);
    return true;
  } catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); return false; }
};

/* Active learned aliases keyed "<aliasRowId>|<BRAND>" → log row (for chip tags). */
export const useLearnedLog = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => axios.get(`${API}/bulk-data/learn-log`, { headers: getAuthHeader() })
    .then(r => setRows(r.data.rows || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  const byChip = Object.fromEntries(rows.filter(r => r.learned === "alias" && r.added).map(r => [`${r.row_id}|${r.brand}`, r]));
  return { rows, byChip, reload: load };
};

export const LearnedChipX = ({ log, onDone }) => (
  <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-emerald-100 px-1 text-[9px] font-sans font-semibold text-emerald-700"
        title={`Learned from Bulk Data by ${log.by_name} — click × to undo`} data-testid={`pa-learned-tag-${log.id}`}>
    learned
    <button type="button" className="-my-0.5 rounded p-0.5 hover:bg-rose-100 hover:text-rose-600" aria-label="Undo learned alias"
            data-testid={`pa-learned-undo-${log.id}`}
            onClick={async (e) => { e.stopPropagation(); if (await undoLearn({ logId: log.id })) onDone(); }}>
      <X className="w-3.5 h-3.5" /></button>
  </span>
);

export const RecentlyLearnedButton = ({ rows, onChanged }) => {
  const [open, setOpen] = useState(false);
  const undo = async (id) => { if (await undoLearn({ logId: id })) onChanged(); };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="pa-recently-learned"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
        <History className="w-4 h-4 mr-1" /> Recently learned ({rows.length})
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="pa-learned-dialog">
          <DialogHeader><DialogTitle>Recently learned generics (last 30 days)</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto text-xs">
            {rows.length === 0 ? <p className="py-6 text-center text-slate-500">Nothing learned in the last 30 days.</p> : (
              <table className="w-full">
                <thead className="sticky top-0 bg-white text-left text-slate-500">
                  <tr><th className="py-1 pr-2">Brand</th><th className="py-1 pr-2">Product</th><th className="py-1 pr-2">Generic</th>
                    <th className="py-1 pr-2">What</th><th className="py-1 pr-2">By / when</th><th /></tr>
                </thead>
                <tbody>{rows.map((r, i) => (
                  <tr key={r.id} className="border-t" data-testid={`pa-learned-row-${i}`}>
                    <td className="py-1 pr-2 font-semibold">{r.brand}</td>
                    <td className="py-1 pr-2 text-slate-600">{r.matched || "—"}</td>
                    <td className="py-1 pr-2 text-emerald-700 font-semibold">{r.generic}
                      {r.learned === "updated" && r.prev_generic && <span className="block text-[10px] text-slate-400">was {r.prev_generic}</span>}</td>
                    <td className="py-1 pr-2">{LEARN_LABEL[r.learned] || r.learned}</td>
                    <td className="py-1 pr-2 text-slate-500">{r.by_name}<br />{new Date(r.created_at).toLocaleString()}</td>
                    <td className="py-1 text-right">
                      <Button size="sm" variant="ghost" className="h-7 text-rose-600" onClick={() => undo(r.id)}
                              data-testid={`pa-learned-undo-btn-${i}`}><Undo2 className="w-3.5 h-3.5 mr-1" />Undo</Button>
                    </td>
                  </tr>))}</tbody>
              </table>)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
